// Block K (UI-22): one expense as a row of the kit's List. On a phone the row keeps its title, its
// approval status and its actions, and line 2 (meta) carries the amount, the date and whether it
// is billable. Tapping the row opens it for editing.
import { memo } from "react";
import { ListRow, RowPrimary, StatusPill, Button, OverflowMenu } from "../../ui";
import { formatMoney } from "../../utils/currency";
import { openStoredFile } from "../../utils/storedFiles";
import { showErrorToast } from "../../utils/toast";
import { messageFromError } from "../../utils/apiError";

export const fmtExpenseDate = str =>
  new Date(str + "T12:00:00").toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });

const APPROVAL = {
  pending:  { tone: "pending", label: "Pending" },
  approved: { tone: "success", label: "Approved" },
  rejected: { tone: "warning", label: "Rejected" },
};

// The file name a receipt downloads as: a data: URI cannot be opened in a new tab (browsers block
// top-level data: navigation), so it is saved instead, with an extension from its type.
function receiptFileName(title, url) {
  const m = /^data:([^;,]+)/.exec(url || "");
  if (!m) return undefined;
  const ext = { "application/pdf": "pdf", "image/png": "png", "image/jpeg": "jpg", "image/jpg": "jpg", "image/webp": "webp", "image/heic": "heic" }[m[1]] || "file";
  const base = String(title || "receipt").replace(/[^\w\- ]+/g, "").trim().slice(0, 60) || "receipt";
  return `${base} receipt.${ext}`;
}

// A receipt in the private file store (Carla's decision 2) is `storedfile:<id>`: it opens by a link
// signed at the moment of opening, never by an href.
export const storedReceiptId = (url) => (typeof url === "string" && url.startsWith("storedfile:") ? url.slice("storedfile:".length) : null);

function openReceipt(title, url) {
  const stored = storedReceiptId(url);
  if (stored) {
    openStoredFile(stored).catch((err) => showErrorToast(messageFromError(err, "That receipt could not be opened.")));
    return;
  }
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  const name = receiptFileName(title, url);
  if (name) a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function ExpenseCard({ columns, expense, currency, onEdit, onDelete, onApprove, onReject, canApprove, projectsLookup, contactsLookup }) {
  const { title, amount, date, category, project_id, contact_id, is_billable, is_billed, receipt_url, approval_status, rejection_reason } = expense;
  const status = approval_status || "approved";
  const pill = APPROVAL[status] || APPROVAL.approved;
  const money = formatMoney(amount, expense.currency || currency);
  const billable = is_billable ? (is_billed ? "Billed" : "Unbilled") : null;
  const pending = canApprove && status === "pending";

  const menu = [
    ...(pending ? [
      { label: "Approve", onSelect: () => onApprove(expense) },
      { label: "Reject", onSelect: () => onReject(expense) },
    ] : []),
    { label: "Edit", onSelect: () => onEdit(expense) },
    // The receipt column is hidden on a phone; the menu carries it there.
    ...(receipt_url ? [{ label: "View receipt", onSelect: () => openReceipt(title, receipt_url) }] : []),
    { label: "Delete", danger: true, onSelect: () => onDelete(expense) },
  ];

  const sub = category || "";
  const none = <span className="exp-none">-</span>;

  return (
    <ListRow
      columns={columns}
      onOpen={() => onEdit(expense)}
      openLabel={`Edit ${title}`}
      meta={[money, fmtExpenseDate(date), billable].filter(Boolean).join(" · ")}
      cells={{
        date: fmtExpenseDate(date),
        expense: (
          <RowPrimary
            name={title}
            sub={status === "rejected" && rejection_reason
              ? <>{sub}{sub ? " · " : ""}<span className="exp-reason">Rejected: {rejection_reason}</span></>
              : sub || undefined}
          />
        ),
        project: project_id ? (projectsLookup[project_id] || none) : none,
        supplier: contact_id ? (contactsLookup[contact_id] || none) : none,
        billable: billable
          ? <StatusPill tone={is_billed ? "success" : "pending"}>{billable}</StatusPill>
          : none,
        amount: <span className="exp-amount">{money}</span>,
        receipt: storedReceiptId(receipt_url)
          ? <button type="button" className="exp-link exp-link--button" onClick={() => openReceipt(title, receipt_url)}>Receipt</button>
          : receipt_url
          ? <a className="exp-link" href={receipt_url} target="_blank" rel="noopener noreferrer"
              download={receiptFileName(title, receipt_url)}>Receipt</a>
          : none,
        status: <StatusPill tone={pill.tone}>{pill.label}</StatusPill>,
        actions: (
          <span className="exp-actions">
            {pending && <Button variant="confirm" size="sm" onClick={() => onApprove(expense)}>Approve</Button>}
            <OverflowMenu label={`More for ${title}`} items={menu} />
          </span>
        ),
      }}
    />
  );
}

export default memo(ExpenseCard);
